/**
 * SubDomainGuard – Erkennt Sub-Domain-Zugriffe und leitet auf die passende
 * Themenwelt-Landing um.
 *
 * Beispiel: Nutzer öffnet `tiershop.deinshop.de/` → Backend liefert den
 * passenden Sub-Shop → wir navigieren auf `/themenwelten/abc-tiershop`
 * (aber nur, wenn wir noch nicht dort sind).
 *
 * DNS-Setup + Reverse-Proxy-Routing (Coolify/Traefik) muss der Betreiber
 * separat einrichten — siehe COOLIFY_DEPLOY.md.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CHECK_CACHE_KEY = 'hs_sub_domain_check_v1';
const CHECK_TTL_MS = 5 * 60 * 1000; // 5 Minuten Cache pro Host

export default function SubDomainGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (checked) return;
    const host = window.location.host || '';
    if (!host) { setChecked(true); return; }
    // Bereits auf einer Themenwelt oder Admin-Bereich → nicht umleiten.
    if (
      location.pathname.startsWith('/themenwelten/') ||
      location.pathname.startsWith('/admin221542')
    ) {
      setChecked(true);
      return;
    }
    // Cache-Check, um nicht bei jedem Route-Wechsel API zu spammen
    try {
      const cached = JSON.parse(sessionStorage.getItem(CHECK_CACHE_KEY) || 'null');
      if (cached && cached.host === host && Date.now() - cached.t < CHECK_TTL_MS) {
        if (cached.slug) redirectIfNeeded(cached.slug);
        setChecked(true);
        return;
      }
    } catch { /* ignore */ }

    axios
      .get(`${API}/api/shop/sub-shops/by-domain/${encodeURIComponent(host)}`)
      .then((r) => {
        const sub = r.data?.sub_shop;
        const slug = sub?.slug || null;
        try {
          sessionStorage.setItem(CHECK_CACHE_KEY, JSON.stringify({ host, slug, t: Date.now() }));
        } catch { /* quota – ignore */ }
        if (slug) redirectIfNeeded(slug);
      })
      .catch(() => {})
      .finally(() => setChecked(true));

    function redirectIfNeeded(slug) {
      // Nur auf `/` und `/shop` umleiten – Deep-Links auf Produktseiten
      // oder Kundenkonto sollen im Sub-Domain-Kontext trotzdem
      // funktionieren.
      if (location.pathname === '/' || location.pathname === '/shop') {
        navigate(`/themenwelten/${slug}`, { replace: true });
      }
    }
  }, []);

  return null;
}
